/**
 * VermoegensaufbauPage – stand-alone marketing page that explains how
 * wealth building through the G21 apartments works.
 *
 * Structure:
 *   1. Hero  (dark, editorial)
 *   2. Problem (inflation erodes cash)
 *   3. Mechanics (4 wealth pillars: Tilgung · Miete · Wertsteigerung · AfA)
 *   4. Scenario Explorer – per-apartment, equity presets (50k · 100k · 200k · Break-Even)
 *   5. 30-year wealth timeline per apartment
 *   6. Financing-partner CTA
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    TrendingUp,
    Home,
    Sparkles,
    ShieldCheck,
    Wallet,
    ChartBar,
    Clock,
    Percent,
    MessageCircle,
    Handshake,
    CheckCircle2,
    XCircle,
    AlertCircle,
    PiggyBank,
    Umbrella,
    Landmark,
    LineChart,
    Coins,
    Building2,
    Zap,
} from "lucide-react";
import { PageShell } from "@/components/site/PageShell";
import { WealthPlanDialog } from "@/components/wealth/WealthPlanDialog";
import { APARTMENTS } from "@/data/apartments";
import { SeoHead } from "@/components/SeoHead";
import { Reveal } from "@/hooks/useReveal";
import { useT, useLang } from "@/i18n/LanguageProvider";

/* ------------------------------------------------------------------ */
/*  helpers                                                           */
/* ------------------------------------------------------------------ */

const SITE = "https://neubau-kaufen-wiesbaden.de";

const SEO = {
    canonical: `${SITE}/vermoegensaufbau`,
    ogImage: `${SITE}/og-cover.jpg`,
    de: {
        title: "Vermögensaufbau mit G21 Wiesbaden · Immobilien-Rechner ab 50.000 € Eigenkapital",
        description:
            "Wie aus einer Eigentumswohnung echter Vermögensaufbau wird: Tilgung, Mietsteigerung, Wertzuwachs, 5 % AfA. Beispielrechnungen für alle 4 G21-Wohnungen, je Eigenkapital-Szenario.",
        ogTitle: "Vermögensaufbau · G21 Wiesbaden · Beispielrechnung pro Wohnung",
        ogDescription:
            "30-Jahre-Rechner: Wie sich Ihr Kapital mit einer G21-Wohnung entwickelt. 4 Wohnungen · 4 Eigenkapital-Szenarien · Break-Even-Jahr auf einen Blick.",
        twitterTitle: "Vermögensaufbau · G21 Wiesbaden",
        twitterDescription:
            "4 Wohnungen · 4 EK-Szenarien · 30-Jahre-Vermögensrechner · Break-Even-Analyse.",
    },
    en: {
        title: "Wealth building with G21 Wiesbaden · Property calculator from €50,000 equity",
        description:
            "How a condominium turns into actual wealth: amortisation, rent growth, appreciation, 5 % depreciation. Example figures for all 4 G21 apartments per equity scenario.",
        ogTitle: "Wealth building · G21 Wiesbaden · per-apartment projection",
        ogDescription:
            "30-year calculator: how your capital develops with a G21 apartment. 4 apartments · 4 equity scenarios · break-even year at a glance.",
        twitterTitle: "Wealth building · G21 Wiesbaden",
        twitterDescription:
            "4 apartments · 4 equity scenarios · 30-year wealth calculator · break-even view.",
    },
};

const resolveSeo = (entry, lang) => ({
    ...(entry[lang] || entry.de),
    ogImage: entry.ogImage,
    canonical: entry.canonical,
    lang,
});

const fmtEUR = (n) =>
    Math.round(n).toLocaleString("de-DE") + " €";

const parseSize = (s) =>
    typeof s === "number" ? s : parseFloat(String(s).replace(",", "."));

/* Break-even computation: earliest 3-year step at which the indexed
 * cold rent covers the monthly annuity.
 * Returns { year, reached, months, monthlyRate } where year=0 means day 1.
 * If not reached within 30 years → reached:false, year:null.                */
const calcBreakEven = ({ price, parking, equity, rate, years, rentPerM2, sizeM2, indexPct = 15, indexEveryYears = 3 }) => {
    const totalCost = (price + parking) * 1.075;
    const financeNeeded = Math.max(0, totalCost - equity);
    const r = rate / 100 / 12;
    const months = years * 12;
    const monthly = financeNeeded > 0
        ? (financeNeeded * r) / (1 - Math.pow(1 + r, -months))
        : 0;
    const rentNow = rentPerM2 * sizeM2;
    if (monthly <= 0 || rentNow >= monthly) {
        return { reached: true, year: 0, monthly, rentNow };
    }
    const factor = 1 + indexPct / 100;
    const steps = Math.ceil(Math.log(monthly / rentNow) / Math.log(factor));
    const year = steps * indexEveryYears;
    if (year > 30) return { reached: false, year: null, monthly, rentNow };
    return { reached: true, year, monthly, rentNow };
};

/* Project the wealth after Y years:
 *  - cumulative rent income (indexed)
 *  - cumulative debt repayment (principal portion of annuity)
 *  - appreciation of the property (we use +2.0 %/yr as conservative default)
 * Returns { paidDown, rentSum, appreciation, finalValue, netWealth } at year Y.     */
const projectWealth = ({ price, parking, equity, rate, years, rentPerM2, sizeM2, indexPct = 15, indexEveryYears = 3, apprPctYr = 2.0, horizonYears = 30 }) => {
    const totalCost = (price + parking);                     // without closing costs for asset value
    const financeNeeded = Math.max(0, totalCost * 1.075 - equity);
    const r = rate / 100 / 12;
    const months = Math.max(1, years * 12);
    const monthly = financeNeeded > 0
        ? (financeNeeded * r) / (1 - Math.pow(1 + r, -months))
        : 0;

    let balance = financeNeeded;
    let paidDown = 0;
    let rentSum = 0;
    const horizonMonths = horizonYears * 12;
    let currentRent = rentPerM2 * sizeM2;

    for (let m = 1; m <= horizonMonths; m++) {
        // Interest & principal within term
        if (m <= months && balance > 0) {
            const interest = balance * r;
            const principal = Math.min(balance, monthly - interest);
            balance -= principal;
            paidDown += principal;
        }
        // Rent indexing every `indexEveryYears` years
        if (m > 1 && (m - 1) % (indexEveryYears * 12) === 0) {
            currentRent = currentRent * (1 + indexPct / 100);
        }
        rentSum += currentRent;
    }

    const appreciation = totalCost * (Math.pow(1 + apprPctYr / 100, horizonYears) - 1);
    const finalValue = totalCost + appreciation;
    const residualDebt = Math.max(0, balance);
    // Net wealth ~= Immobilienwert (heute + Wertsteigerung) - Restschuld + kumulierte Nettomieten
    // For this page we simplify: netWealth shown = Tilgung + Wertsteigerung + (Mieteinnahmen - Annuität, kappt bei 0)
    const annuityPaid = Math.min(months, horizonMonths) * monthly;
    const cashflowSum = rentSum - annuityPaid;                // can be negative in early years
    const netWealth = paidDown + appreciation + Math.max(0, cashflowSum);

    return {
        monthly,
        paidDown,
        rentSum,
        annuityPaid,
        cashflowSum,
        appreciation,
        finalValue,
        residualDebt,
        netWealth,
    };
};

/* ------------------------------------------------------------------ */
/*  Scenario constants                                                 */
/* ------------------------------------------------------------------ */

const RENT_PER_M2 = 18;
const DEFAULT_RATE = 3.6;
const DEFAULT_TERM = 30;

const SCENARIOS = [
    { id: "50k",   equity:  50000, labelDe: "50.000 €",  labelEn: "€50,000"  },
    { id: "100k",  equity: 100000, labelDe: "100.000 €", labelEn: "€100,000" },
    { id: "200k",  equity: 200000, labelDe: "200.000 €", labelEn: "€200,000" },
    { id: "be",    equity: null,   labelDe: "Break-Even EK", labelEn: "Break-even equity" },
];

/**
 * Equity needed so annuity equals current cold rent (rounded up to next 5 k).
 */
const breakEvenEquity = (apt) => {
    const totalCost = (apt.priceValue + apt.parkingPrice) * 1.075;
    const target = RENT_PER_M2 * parseSize(apt.size);
    const r = DEFAULT_RATE / 100 / 12;
    const months = DEFAULT_TERM * 12;
    const annFactor = r / (1 - Math.pow(1 + r, -months));
    const financeNeeded = target / annFactor;
    return Math.max(0, Math.ceil((totalCost - financeNeeded) / 5000) * 5000);
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function VermoegensaufbauPage() {
    const { lang } = useLang();

    return (
        <PageShell testId="vermoegensaufbau-page">
            <SeoHead seo={resolveSeo(SEO, lang)} />
            <Hero />
            <Reveal><Problem /></Reveal>
            <Reveal><Mechanics /></Reveal>
            <Reveal><AltersvorsorgeVergleich /></Reveal>
            <Reveal><ScenarioExplorer /></Reveal>
            <Reveal><WealthTimeline /></Reveal>
            <Reveal><FinanceCta /></Reveal>
        </PageShell>
    );
}

/* ================================================================== */
/*  SECTIONS                                                            */
/* ================================================================== */

const Hero = () => {
    const t = useT();
    return (
        <section className="relative bg-sage-600 rounded-b-[2.5rem] sm:rounded-b-[3.5rem] pt-16 pb-20 sm:pt-24 sm:pb-28 overflow-hidden" data-testid="wealth-hero">
            {/* background accent glows */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-[-10%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-terracotta-300/10 blur-3xl" />
                <div className="absolute bottom-[-20%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-mint-200/10 blur-3xl" />
            </div>
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
                <div className="grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="inline-flex items-center gap-2 mb-6 reveal-up">
                            <span className="inline-block w-8 h-px bg-terracotta-300" />
                            <span className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-300">
                                {t({ de: "Vermögensaufbau · G21 Wiesbaden", en: "Wealth building · G21 Wiesbaden" })}
                            </span>
                        </div>
                        <h1 className="font-display font-black tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance reveal-up" style={{ animationDelay: "80ms" }}>
                            {t({ de: "Heute eine Wohnung.", en: "Today an apartment." })}
                            <br />
                            <span className="text-terracotta-300">
                                {t({ de: "Morgen ein Vermögen.", en: "Tomorrow wealth." })}
                            </span>
                        </h1>
                        <p className="font-body text-lg text-white/75 mt-7 max-w-2xl leading-relaxed reveal-up" style={{ animationDelay: "160ms" }}>
                            {t({
                                de: "Eine Neubau-Eigentumswohnung ist mehr als ein Zuhause oder Mieteinnahme – sie ist ein Werkzeug für echten Vermögensaufbau. Vier Kräfte arbeiten parallel für Sie: die Miete zahlt die Tilgung, die Miete selbst steigt, die Immobilie wertet auf, das Finanzamt beteiligt sich. Auf dieser Seite zeigen wir Ihnen, wie genau das bei jeder der vier G21-Wohnungen aussieht – auch wenn Sie ein kleineres Eigenkapital mitbringen.",
                                en: "A new-build condominium is more than a home or rent receipt – it is a tool for building real wealth. Four forces work in parallel: rent pays down debt, rent itself grows, the property appreciates, the tax office participates. This page shows you how that plays out for every one of the four G21 apartments – even if you bring only modest equity.",
                            })}
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4 reveal-up" style={{ animationDelay: "240ms" }}>
                            <a
                                href="#szenarien"
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-terracotta-300 text-sage-700 font-body font-medium hover:bg-terracotta-200 transition-colors"
                                data-testid="wealth-cta-scenarios"
                            >
                                <ChartBar size={16} />
                                {t({ de: "Meine Szenarien ansehen", en: "View my scenarios" })}
                                <ArrowRight size={16} />
                            </a>
                            <Link
                                to="/investor"
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white hover:border-terracotta-300 hover:text-terracotta-300 transition-colors"
                                data-testid="wealth-cta-mortgage"
                            >
                                <Home size={16} />
                                {t({ de: "Zum Finanzierungs-Rechner", en: "To the financing calculator" })}
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="rounded-3xl border border-white/15 bg-white/5 backdrop-blur-sm p-8 reveal-up" style={{ animationDelay: "300ms" }}>
                            <div className="font-body text-xs text-white/55 uppercase tracking-[0.18em]">
                                {t({ de: "Ein Beispiel", en: "Example" })}
                            </div>
                            <div className="font-display font-black text-4xl text-terracotta-300 mt-3">
                                +1.131.500 €
                            </div>
                            <p className="font-body text-sm text-white/75 mt-2 leading-relaxed">
                                {t({
                                    de: (
                                        <>
                                            Netto-Vermögenszuwachs nach <strong>30 Jahren</strong> bei der Erdgeschoss-Wohnung mit nur{" "}
                                            <strong>50.000 € Eigenkapital</strong>. Tilgung + Wertsteigerung + kumulierter Cashflow.
                                        </>
                                    ),
                                    en: (
                                        <>
                                            Net wealth gain after <strong>30 years</strong> on the ground-floor apartment with only{" "}
                                            <strong>€50,000 equity</strong>. Repayment + appreciation + cumulative cashflow.
                                        </>
                                    ),
                                })}
                            </p>
                            <p className="font-body text-[11px] text-white/50 mt-3 leading-relaxed">
                                {t({
                                    de: "Annahmen: 18 €/m² Kaltmiete, +15 % alle 3 Jahre, 3,6 % Zins, 2 % Wertsteigerung p.a. Unverbindlich.",
                                    en: "Assumptions: €18/sqm cold rent, +15 % every 3 years, 3.6 % rate, 2 % appreciation p.a. Non-binding.",
                                })}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Problem = () => {
    const t = useT();
    return (
        <section className="py-24 sm:py-32" data-testid="wealth-problem">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <span className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-500">
                            {t({ de: "Der Punkt ist:", en: "The point is:" })}
                        </span>
                        <h2 className="font-display font-black tracking-tight text-sage-500 text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight text-balance">
                            {t({ de: "Sparbuch verliert.", en: "Savings accounts lose." })}
                            <span className="text-terracotta-400"> {t({ de: "Jeden Tag.", en: "Every day." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="font-body text-lg text-sage-500/75 leading-relaxed">
                            {t({
                                de: "Bei 2 % Inflation im langjährigen Schnitt verliert ein Guthaben von 100.000 € auf dem Tagesgeld in 30 Jahren über die Hälfte seiner Kaufkraft – selbst wenn es nominell gleich bleibt. Die Mieten steigen weiter. Die Baukosten steigen weiter. Wer heute nichts besitzt, zahlt später einfach mehr.",
                                en: "At a long-term 2 % inflation, €100,000 on a savings account loses more than half of its purchasing power over 30 years – even at nominal face value. Rents keep rising. Construction costs keep rising. Those who own nothing today just pay more later.",
                            })}
                        </p>
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <Metric label={t({ de: "Sparbuch 30 J · real", en: "Savings 30 yrs · real" })} value="−45 %" tone="danger" />
                            <Metric label={t({ de: "Miete Deutschland · 10 J", en: "Rent Germany · 10 yrs" })} value="+64 %" tone="danger" />
                            <Metric label={t({ de: "Baupreis · 10 J", en: "Construction · 10 yrs" })} value="+54 %" tone="danger" />
                        </div>
                        <p className="font-body text-[11px] text-sage-500/50 mt-4 leading-relaxed">
                            {t({
                                de: "Quellen: Destatis Verbraucherpreisindex, Bulwiengesa-Mietpreisindex, Destatis Baupreisindex (10 Jahre 2014–2024).",
                                en: "Sources: Destatis CPI, Bulwiengesa rent index, Destatis construction-price index (10 yrs 2014–2024).",
                            })}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Mechanics = () => {
    const t = useT();
    const pillars = [
        {
            icon: Wallet,
            title: t({ de: "Der Mieter tilgt.", en: "The tenant pays down." }),
            body: t({
                de: "Die monatliche Kaltmiete fließt zu einem großen Teil direkt in die Tilgung Ihres Darlehens. Je länger, desto stärker – denn der Zinsanteil sinkt kontinuierlich.",
                en: "A large share of the monthly cold rent goes straight into paying down your loan. The effect grows over time as the interest share shrinks.",
            }),
        },
        {
            icon: TrendingUp,
            title: t({ de: "Die Miete steigt.", en: "Rent grows." }),
            body: t({
                de: "In A-Lagen mit Neubau-Premium steigen die Kaltmieten erfahrungsgemäß um ca. +15 % alle 3 Jahre. Nach 15 Jahren deckt die Kaltmiete in unseren Szenarien locker die feste Rate – positiver Cashflow.",
                en: "Prime locations with new-build premium typically see cold rent grow by ~ +15 % every 3 years. After 15 years the cold rent easily covers the fixed rate – positive cashflow.",
            }),
        },
        {
            icon: Home,
            title: t({ de: "Die Immobilie wächst.", en: "The property appreciates." }),
            body: t({
                de: "Wiesbaden wächst. Neubau-Wohnungen in A-Lage steigen konservativ gerechnet mit +2 % p.a. im Wert. Nach 30 Jahren ergibt das eine Wertsteigerung von rund +81 % – unabhängig von Miete und Tilgung.",
                en: "Wiesbaden is growing. Prime new-build typically appreciates by ~ +2 % per year. Over 30 years this compounds to ~ +81 % – independent of rent and repayment.",
            }),
        },
        {
            icon: Percent,
            title: t({ de: "Der Staat beteiligt sich.", en: "The state participates." }),
            body: t({
                de: "Mit der 5 % degressiven AfA für Neubau-Effizienzhäuser 40 ab 2026 plus KfW-Förderung kommt ein erheblicher steuerlicher Hebel dazu. Zusammen mit abzugsfähigen Schuldzinsen senkt das Ihre effektive Finanzierungslast.",
                en: "Via 5 % declining-balance depreciation for new-build energy-40 homes from 2026 plus KfW funding, a significant tax lever comes on top. Combined with deductible interest this lowers your effective financing burden.",
            }),
        },
    ];
    return (
        <section className="py-24 sm:py-32 bg-white border-y border-black/5" data-testid="wealth-mechanics">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-10 mb-14">
                    <div className="lg:col-span-5">
                        <span className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-500">
                            {t({ de: "Die Mechanik", en: "The mechanics" })}
                        </span>
                        <h2 className="font-display font-black tracking-tight text-sage-500 text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight text-balance">
                            {t({ de: "Vier Kräfte, die", en: "Four forces that" })}
                            <span className="text-terracotta-400"> {t({ de: "parallel arbeiten.", en: "work in parallel." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 self-end">
                        <p className="font-body text-lg text-sage-500/75 leading-relaxed">
                            {t({
                                de: "Eine Immobilie als Kapitalanlage ist kein Renditeobjekt mit nur einer Zahl am Ende. Vier unabhängige Effekte addieren sich über die Jahre zu einem Vermögensaufbau, den Aktien oder Gold in dieser Kombination selten erreichen.",
                                en: "Investment property is not a one-number return game. Four independent effects stack over the years into a wealth trajectory that stocks or gold rarely match in this combination.",
                            })}
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    {pillars.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <div key={i} className="bg-mint-50 rounded-3xl p-8 lg:p-10">
                                <div className="flex items-center gap-3 mb-5">
                                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-sage-500">
                                        <Icon size={20} strokeWidth={1.7} />
                                    </div>
                                    <span className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-500">{`0${i + 1}`}</span>
                                </div>
                                <h3 className="font-display font-bold text-2xl text-sage-500">{p.title}</h3>
                                <p className="font-body text-base text-sage-500/75 mt-3 leading-relaxed">{p.body}</p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

/* --------------------------------------------------------------- */
/*  ALTERSVORSORGE VERGLEICH                                        */
/* --------------------------------------------------------------- */

const VORSORGE_OPTIONS = [
    {
        id: "sparbuch",
        icon: PiggyBank,
        name: "Sparbuch / Tagesgeld",
        headline: "Kaufkraft-Verlust durch Inflation",
        rendite: { value: "0,5 – 2 %", tone: "bad" },
        infl:    { value: "nein",        tone: "bad", note: "Real −2 % p.a." },
        hebel:   { value: "kein",        tone: "bad" },
        access:  { value: "sofort",      tone: "good" },
        steuer:  {
            tone: "bad",
            headline: "Zinsen voll steuerpflichtig",
            body: "Alle Zinsen unterliegen der Abgeltungsteuer von 25 % (+ Soli, ggf. Kirchensteuer) oberhalb des Sparerpauschbetrags (1.000 € / 2.000 € Verheiratete).",
        },
        pros: [
            "Jederzeit verfügbar",
            "Einlagensicherung bis 100.000 €",
        ],
        cons: [
            "Zins meist unter Inflationsrate → realer Verlust",
            "100.000 € werden in 30 Jahren real nur noch ~45.000 € wert sein",
            "Keine Hebelwirkung, kein Sachwert, keine Steuervorteile",
        ],
        verdict: "Geeignet als Notgroschen (3–6 Monatsausgaben), ungeeignet für langfristigen Vermögensaufbau.",
        score: 2,
    },
    {
        id: "lv",
        icon: Umbrella,
        name: "Lebens-/Rentenversicherung",
        headline: "Niedrige Rendite, hohe Kosten",
        rendite: { value: "ca. 2 %",    tone: "bad" },
        infl:    { value: "gering",     tone: "bad" },
        hebel:   { value: "kein",       tone: "bad" },
        access:  { value: "gebunden",   tone: "bad", note: "12+ Jahre" },
        steuer: {
            tone: "ok",
            headline: "Halbeinkünfte-Verfahren bei 12+ Jahren",
            body: "Nur die Hälfte des Ertrags ist zu versteuern, wenn die Police 12+ Jahre lief und die Auszahlung nach dem 62. Lebensjahr erfolgt. Sonst Abgeltungsteuer auf vollen Gewinn.",
        },
        pros: [
            "Kombination aus Sparen und Versicherungsschutz",
            "Steuerbegünstigt bei 12+ Jahren Laufzeit (Halbeinkünfteverfahren)",
        ],
        cons: [
            "Hohe Abschluss- und Verwaltungskosten (5–10 % der Beiträge)",
            "Garantiezins mittlerweile nur noch 0,25 %",
            "Lange vertragliche Bindung, hoher Verlust bei Kündigung",
        ],
        verdict: "Von unabhängigen Verbraucherschützern seit Jahren kritisch gesehen. Selten die effizienteste Form.",
        score: 2,
    },
    {
        id: "riester",
        icon: Landmark,
        name: "Riester-Rente",
        headline: "Komplex, teuer, nachgelagert besteuert",
        rendite: { value: "ca. 1 – 2 %", tone: "bad" },
        infl:    { value: "gering",      tone: "bad" },
        hebel:   { value: "kein",        tone: "bad" },
        access:  { value: "gebunden",    tone: "bad", note: "bis Rente" },
        steuer: {
            tone: "ok",
            headline: "Vorne gefördert, hinten besteuert",
            body: "Beiträge bis 2.100 €/Jahr steuerlich absetzbar (Sonderausgabenabzug) plus staatliche Zulagen. ABER: Spätere Rentenzahlungen werden vollständig mit dem persönlichen Einkommensteuersatz nachgelagert besteuert.",
        },
        pros: [
            "Staatliche Zulagen (175 €/Jahr + Kinderzulagen)",
            "Beiträge steuerlich absetzbar (Sonderausgabenabzug)",
        ],
        cons: [
            "Hohe Bürokratie, jährliche Nachweise erforderlich",
            "Vollständige nachgelagerte Besteuerung in der Rentenphase",
            "Oft hohe Gebühren bei Fonds-basierten Riester-Produkten",
            "Reformen drohen seit Jahren – Rechtsunsicherheit",
        ],
        verdict: "Für wenige Konstellationen (viele Kinder, hohe Zulagen) attraktiv. Für die meisten ineffizient.",
        score: 2,
    },
    {
        id: "etf",
        icon: LineChart,
        name: "ETF-Sparplan (MSCI World)",
        headline: "Solide, aber ohne Hebelwirkung",
        rendite: { value: "ca. 6 – 7 %", tone: "ok" },
        infl:    { value: "ja (indirekt)", tone: "ok" },
        hebel:   { value: "kein",         tone: "bad" },
        access:  { value: "sofort",       tone: "good" },
        steuer: {
            tone: "ok",
            headline: "Abgeltungsteuer + Vorabpauschale",
            body: "Kursgewinne und Ausschüttungen unterliegen der Abgeltungsteuer (25 % + Soli, ggf. KiSt). Bei thesaurierenden Fonds wird jährlich eine Vorabpauschale besteuert. Teilfreistellung von 30 % bei Aktien-ETFs.",
        },
        pros: [
            "Historisch 6–7 % Rendite p.a. bei breiter Streuung",
            "Sehr niedrige Kosten (ca. 0,2 % TER)",
            "Teilfreistellung 30 % bei Aktien-ETFs",
            "Jederzeit verkäuflich, hohe Flexibilität",
        ],
        cons: [
            "Hohe Volatilität – Kursrückgänge von 30–50 % möglich",
            "Kein Hebel: 100 € Einzahlung = 100 € arbeitendes Kapital",
            "Keine Inflationsschutz-Garantie, aber langfristig positiv korreliert",
        ],
        verdict: "Eine sehr gute Basis für jedes Portfolio – aber ohne Finanzierungshebel deutlich langsamer als eine Immobilie.",
        score: 3,
    },
    {
        id: "gold",
        icon: Coins,
        name: "Gold / Edelmetalle",
        headline: "Werterhalt, aber keine Einnahmen",
        rendite: { value: "ca. 2 – 4 %", tone: "ok" },
        infl:    { value: "ja",          tone: "good" },
        hebel:   { value: "kein",        tone: "bad" },
        access:  { value: "sofort",      tone: "good" },
        steuer: {
            tone: "good",
            headline: "Nach 1 Jahr Haltedauer steuerfrei",
            body: "Kursgewinne auf physisches Gold sind nach einer Haltedauer von 12 Monaten vollständig steuerfrei (private Veräußerungsgeschäfte, § 23 EStG). Gold-ETCs sind je nach Produkt unterschiedlich zu behandeln.",
        },
        pros: [
            "Traditioneller Wertspeicher in Krisenzeiten",
            "Inflationsschutz über lange Zeiträume",
            "Steuerfrei nach 1 Jahr Haltedauer (physisch)",
        ],
        cons: [
            "Keine laufenden Einnahmen (Miete, Zins, Dividende)",
            "Hohe Spreads zwischen An- und Verkaufspreis (5–10 %)",
            "Lagerungskosten (Schließfach/Tresor)",
            "Renditen schwanken stark je nach Zeitraum",
        ],
        verdict: "Guter Beimischung (5–10 % im Portfolio), kein Vermögensaufbau-Motor.",
        score: 3,
    },
    {
        id: "g21",
        icon: Building2,
        name: "Neubau-Immobilie G21",
        headline: "Vier Kräfte arbeiten parallel für Sie",
        rendite: { value: "× 10 – × 22 auf EK", tone: "good" },
        infl:    { value: "ja",                 tone: "good" },
        hebel:   { value: "ja (Bank)",          tone: "good" },
        access:  { value: "30 J Plan",          tone: "ok" },
        steuer: {
            tone: "good",
            headline: "Starke steuerliche Dämpfung",
            body: "Schuldzinsen und laufende Kosten sind als Werbungskosten voll abziehbar. Die 5 % degressive AfA für Neubau-Effizienzhäuser 40 (ab 2026) führt in den ersten Jahren zu hohen Abschreibungen – oft entsteht steuerlich ein Verlust, der mit anderen Einkünften verrechnet wird. Nach 10 Jahren Haltedauer sind Wertsteigerungen i.d.R. steuerfrei (§ 23 EStG).",
            highlights: [
                "5 % degressive AfA (Neubau EH 40, ab 2026)",
                "Schuldzinsen voll abziehbar",
                "Verlustverrechnung mit anderen Einkünften",
                "Wertsteigerung nach 10 J steuerfrei",
            ],
        },
        pros: [
            "Tilgung wird vom Mieter gezahlt – Sie bauen Eigentum auf ohne eigenen Cashflow",
            "Mietwachstum (+15 % alle 3 Jahre in A-Lage) – Inflation arbeitet für Sie",
            "Wertsteigerung + 5 % degressive AfA ab 2026 + KfW-Förderung",
            "Finanzierungshebel: 100.000 € EK steuern eine 700.000 € Anlage",
        ],
        cons: [
            "Langfristige Bindung – kurzfristige Liquidität begrenzt",
            "Verwaltungsaufwand (oder Vermietungsservice nutzen)",
            "Marktrisiken bei Mikro-Lage und Zinsumfeld",
        ],
        verdict: "Der einzige Vermögensaufbau-Ansatz, der Tilgung, Wertsteigerung, Mietwachstum und Steuereffekt parallel kombiniert.",
        score: 5,
        highlight: true,
    },
];

const TONE_MAP = {
    good: "text-emerald-400",
    ok:   "text-amber-400",
    bad:  "text-rose-400",
};

const TONE_ICON = {
    good: CheckCircle2,
    ok:   AlertCircle,
    bad:  XCircle,
};

const AltersvorsorgeVergleich = () => {
    const t = useT();
    const [selectedId, setSelectedId] = useState("sparbuch");
    const selected = VORSORGE_OPTIONS.find((o) => o.id === selectedId) || VORSORGE_OPTIONS[0];
    const Icon = selected.icon;

    return (
        <section className="py-24 sm:py-32 bg-sage-600 text-white" id="altersvorsorge-vergleich" data-testid="altersvorsorge-vergleich">
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-10 mb-12">
                    <div className="lg:col-span-5">
                        <span className="overline text-terracotta-300">
                            {t({ de: "Ehrlicher Vergleich", en: "Honest comparison" })}
                        </span>
                        <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight mt-4 text-white text-balance">
                            {t({ de: "Warum nicht einfach", en: "Why not just" })}
                            <span className="text-terracotta-300"> {t({ de: "sparen?", en: "save?" })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="body-md text-white/75">
                            {t({
                                de: "Vergleich zu klassischen Altersvorsorge-Formen – fair und ohne Schönfärberei. Klicken Sie auf eine Option, um Details zu sehen.",
                                en: "Honest comparison with classical retirement options. Click an option to see details.",
                            })}
                        </p>
                    </div>
                </div>

                {/* Options grid selector */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-10" role="tablist">
                    {VORSORGE_OPTIONS.map((opt) => {
                        const OptIcon = opt.icon;
                        const isActive = selectedId === opt.id;
                        return (
                            <button
                                key={opt.id}
                                onClick={() => setSelectedId(opt.id)}
                                className={`group flex flex-col items-start gap-2 p-4 rounded-2xl border transition-colors text-left min-w-0 ${
                                    isActive
                                        ? opt.highlight
                                            ? "border-terracotta-500 bg-terracotta-500 text-white"
                                            : "border-white/60 bg-white/10 text-white"
                                        : opt.highlight
                                            ? "border-terracotta-500/50 bg-transparent text-white hover:bg-terracotta-500/10"
                                            : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"
                                }`}
                                aria-pressed={isActive}
                                data-testid={`vorsorge-tab-${opt.id}`}
                            >
                                <OptIcon size={18} className={opt.highlight ? "text-terracotta-300" : ""} />
                                <span className="font-display font-bold text-sm leading-tight break-words hyphens-auto max-w-full">{opt.name}</span>
                                {opt.highlight && (
                                    <span className="text-[10px] font-body uppercase tracking-[0.12em] text-terracotta-300">
                                        {t({ de: "Unser Ansatz", en: "Our approach" })}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Detail panel */}
                <div className={`rounded-3xl border ${selected.highlight ? "border-terracotta-500" : "border-white/20"} p-8 lg:p-12`} data-testid="vorsorge-detail">
                    <div className="grid lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-5">
                            <div className="flex items-center gap-3 mb-4">
                                <div className={`p-3 ${selected.highlight ? "bg-terracotta-500 text-white" : "bg-white/10 text-white"}`}>
                                    <Icon size={22} />
                                </div>
                                <span className="overline text-terracotta-300">
                                    {selected.highlight ? t({ de: "Unser Ansatz", en: "Our approach" }) : t({ de: "Alternative", en: "Alternative" })}
                                </span>
                            </div>
                            <h3 className="font-display font-bold text-3xl sm:text-4xl text-white leading-tight">
                                {selected.name}
                            </h3>
                            <p className={`body-md mt-4 ${selected.highlight ? "text-terracotta-300" : "text-white/70"}`}>
                                {selected.headline}
                            </p>

                            {/* Rating scale */}
                            <div className="mt-8 pt-6 border-t border-white/15">
                                <div className="font-body text-[10px] uppercase tracking-[0.18em] text-white/55">
                                    {t({ de: "G21-Bewertung · 1–5", en: "G21 rating · 1–5" })}
                                </div>
                                <div className="flex gap-1.5 mt-3">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <div
                                            key={n}
                                            className={`h-2 flex-1 ${
                                                n <= selected.score
                                                    ? selected.highlight
                                                        ? "bg-terracotta-300"
                                                        : "bg-white/70"
                                                    : "bg-white/10"
                                            }`}
                                        />
                                    ))}
                                </div>
                                <div className="font-body text-xs text-white/55 mt-2">
                                    {selected.verdict}
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-7">
                            {/* Stat grid */}
                            <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
                                <StatCell label="Rendite p.a." {...selected.rendite} />
                                <StatCell label="Inflationsschutz" {...selected.infl} />
                                <StatCell label="Hebelwirkung" {...selected.hebel} />
                                <StatCell label="Verfügbarkeit" {...selected.access} />
                            </div>

                            {/* Pros / Cons */}
                            <div className="mt-8 grid md:grid-cols-2 gap-6">
                                <div>
                                    <div className="flex items-center gap-2 mb-4">
                                        <CheckCircle2 size={16} className="text-emerald-400" />
                                        <span className="overline text-emerald-400">Vorteile</span>
                                    </div>
                                    <ul className="space-y-2.5">
                                        {selected.pros.map((p, i) => (
                                            <li key={i} className="flex items-start gap-2 font-body text-sm text-white/85">
                                                <span className="text-emerald-400 mt-0.5">+</span>
                                                <span>{p}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-4">
                                        <XCircle size={16} className="text-rose-400" />
                                        <span className="overline text-rose-400">Nachteile</span>
                                    </div>
                                    <ul className="space-y-2.5">
                                        {selected.cons.map((c, i) => (
                                            <li key={i} className="flex items-start gap-2 font-body text-sm text-white/75">
                                                <span className="text-rose-400 mt-0.5">−</span>
                                                <span>{c}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            {/* Steuerliche Dämpfung */}
                            {selected.steuer && (
                                <div
                                    className={`mt-8 p-5 rounded-2xl border ${
                                        selected.steuer.tone === "good"
                                            ? "bg-terracotta-500/10 border-terracotta-500/40"
                                            : "bg-white/5 border-white/15"
                                    }`}
                                    data-testid="vorsorge-steuer"
                                >
                                    <div className="flex items-center gap-2">
                                        <Percent size={14} className={TONE_MAP[selected.steuer.tone] || "text-white/60"} />
                                        <span className={`overline ${TONE_MAP[selected.steuer.tone] || "text-white/60"}`}>
                                            Steuerliche Dämpfung
                                        </span>
                                    </div>
                                    <div className="font-display font-bold text-lg text-white mt-2">
                                        {selected.steuer.headline}
                                    </div>
                                    <p className="body-md text-sm text-white/75 mt-2 leading-relaxed">
                                        {selected.steuer.body}
                                    </p>
                                    {selected.steuer.highlights && (
                                        <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                                            {selected.steuer.highlights.map((h, i) => (
                                                <li key={i} className="flex items-center gap-2 font-body text-xs text-white/85">
                                                    <CheckCircle2 size={12} className="text-terracotta-300 shrink-0" />
                                                    <span>{h}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {selected.highlight && (
                        <div className="mt-8 pt-8 border-t border-terracotta-500/30 flex flex-wrap items-center justify-between gap-4">
                            <p className="font-body text-sm text-white/80 max-w-2xl">
                                {t({
                                    de: "Rechnen Sie jetzt durch, was Ihre Zahlen wären. Kostenlos, ohne Registrierung.",
                                    en: "Calculate your own numbers now. Free, no registration.",
                                })}
                            </p>
                            <a
                                href="#szenarien"
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-terracotta-300 text-sage-700 font-body font-medium hover:bg-terracotta-200 transition-colors"
                                data-testid="vorsorge-cta-szenarien"
                            >
                                <ChartBar size={16} />
                                {t({ de: "Meine Zahlen berechnen", en: "Calculate my numbers" })}
                            </a>
                        </div>
                    )}
                </div>

                <div className="mt-8 p-6 rounded-2xl border border-white/15 bg-white/5" data-testid="vorsorge-disclaimer">
                    <div className="flex items-start gap-3">
                        <AlertCircle size={18} className="text-amber-400 shrink-0 mt-0.5" />
                        <div>
                            <div className="font-display font-bold text-base text-white">
                                {t({ de: "Haftungsausschluss & rechtlicher Hinweis", en: "Disclaimer & legal notice" })}
                            </div>
                            <p className="font-body text-xs text-white/65 mt-2 leading-relaxed">
                                {t({
                                    de: (
                                        <>
                                            Die hier dargestellten Informationen dienen ausschließlich der allgemeinen Orientierung.
                                            Renditeangaben beruhen auf historischen Durchschnittswerten und können sich jederzeit ändern.
                                            Der Finanzierungshebel einer Immobilie wirkt <strong>in beide Richtungen</strong>.
                                            <br /><br />
                                            <strong className="text-white">Diese Seite ersetzt keine individuelle steuerliche, rechtliche oder finanzielle Beratung.</strong>{" "}
                                            Steuerliche Effekte (u.a. AfA, Werbungskosten, Halbeinkünfteverfahren, Abgeltungsteuer,
                                            nachgelagerte Besteuerung, Spekulationsfrist) hängen von Ihrer persönlichen Situation ab und können sich
                                            durch Gesetzesänderungen ändern. Für eine verbindliche Einschätzung konsultieren Sie
                                            bitte einen <strong className="text-white">Steuerberater, einen Rechtsanwalt oder einen unabhängigen Finanzberater</strong>.
                                            <br /><br />
                                            Die G21 Projekt GmbH, die Betreiber dieser Website sowie deren Vertreter übernehmen <strong>keine Haftung</strong>{" "}
                                            für die Richtigkeit, Vollständigkeit oder Aktualität der dargestellten Informationen und lehnen
                                            jede Haftung für Entscheidungen ab, die auf Basis dieser Seite getroffen werden.
                                        </>
                                    ),
                                    en: (
                                        <>
                                            The information shown here is for general guidance only. Return figures are historical averages
                                            and may change at any time. Leverage of a real-estate financing works <strong>both ways</strong>.
                                            <br /><br />
                                            <strong className="text-white">This page does not replace individual tax, legal or financial advice.</strong>{" "}
                                            Tax effects depend on your personal situation and may change with legislation. Please consult a
                                            tax advisor, lawyer or independent financial advisor for binding guidance.
                                            <br /><br />
                                            G21 Projekt GmbH and the operators of this website assume <strong>no liability</strong> for the accuracy,
                                            completeness or timeliness of the information presented, and decline any liability for decisions
                                            taken on the basis of this page.
                                        </>
                                    ),
                                })}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const StatCell = ({ label, value, tone = "ok", note }) => {
    const Icon = TONE_ICON[tone] || AlertCircle;
    return (
        <div className="bg-sage-600 p-5">
            <div className="flex items-center justify-between">
                <span className="font-body text-[10px] uppercase tracking-[0.16em] text-white/55">{label}</span>
                <Icon size={14} className={TONE_MAP[tone] || "text-white/50"} />
            </div>
            <div className={`font-display font-bold text-xl mt-2 ${TONE_MAP[tone] || "text-white"}`}>{value}</div>
            {note && <div className="font-body text-[11px] text-white/45 mt-1">{note}</div>}
        </div>
    );
};

/* Tax dampening – year-1 estimate of the deductible loss generated by
 * the 5 % declining-balance AfA on the building value (80 % of asset) plus
 * mortgage interest. Positive "loss" × personal tax rate yields the rough
 * annual income-tax saving. Rent reduces the deductible amount.       */
const calcTaxSavingYear1 = ({ price, parking, equity, rate, years, rentPerM2, sizeM2, taxRatePct }) => {
    const assetCost = price + parking;
    const buildingShare = assetCost * 0.80;                            // Gebäudewert
    const afaYear1 = buildingShare * 0.05;                             // 5 % degressiv Jahr 1
    const financeNeeded = Math.max(0, assetCost * 1.075 - equity);
    const interestYear1 = financeNeeded * (rate / 100);                // grobe Näherung für Jahr 1
    const annualRent = rentPerM2 * sizeM2 * 12;
    const taxableLoss = afaYear1 + interestYear1 - annualRent;         // kann negativ (Verlust) sein
    const deductible = Math.max(0, taxableLoss);
    const saving = deductible * (taxRatePct / 100);
    return {
        buildingShare,
        afaYear1,
        interestYear1,
        annualRent,
        taxableLoss,
        deductible,
        saving,
    };
};

/* --------------------------------------------------------------- */
/*  SCENARIO EXPLORER                                               */
/* --------------------------------------------------------------- */

const ScenarioExplorer = () => {
    const t = useT();
    const [aptId, setAptId] = useState("erdgeschoss");
    const [scenarioId, setScenarioId] = useState("50k");
    const [planOpen, setPlanOpen] = useState(false);
    const [taxRatePct, setTaxRatePct] = useState(42);

    const apt = APARTMENTS.find((a) => a.id === aptId) || APARTMENTS[0];
    const size = parseSize(apt.size);

    const equity = useMemo(() => {
        const sc = SCENARIOS.find((s) => s.id === scenarioId);
        if (!sc) return 50000;
        if (sc.equity === null) return breakEvenEquity(apt);
        return sc.equity;
    }, [scenarioId, apt]);

    const be = useMemo(
        () => calcBreakEven({
            price: apt.priceValue,
            parking: apt.parkingPrice,
            equity,
            rate: DEFAULT_RATE,
            years: DEFAULT_TERM,
            rentPerM2: RENT_PER_M2,
            sizeM2: size,
        }),
        [apt, equity, size],
    );

    const proj = useMemo(
        () => projectWealth({
            price: apt.priceValue,
            parking: apt.parkingPrice,
            equity,
            rate: DEFAULT_RATE,
            years: DEFAULT_TERM,
            rentPerM2: RENT_PER_M2,
            sizeM2: size,
            horizonYears: 30,
        }),
        [apt, equity, size],
    );

    const tax = useMemo(
        () => calcTaxSavingYear1({
            price: apt.priceValue,
            parking: apt.parkingPrice,
            equity,
            rate: DEFAULT_RATE,
            years: DEFAULT_TERM,
            rentPerM2: RENT_PER_M2,
            sizeM2: size,
            taxRatePct,
        }),
        [apt, equity, size, taxRatePct],
    );

    return (
        <section id="szenarien" className="py-24 sm:py-32 bg-white text-sage-700 border-y border-black/5" data-testid="wealth-scenarios">
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-10 mb-12">
                    <div className="lg:col-span-5">
                        <span className="overline text-terracotta-500">{t({ de: "Ihre Szenarien", en: "Your scenarios" })}</span>
                        <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight mt-4 text-balance">
                            {t({ de: "So sehen Ihre", en: "Here is how your" })}
                            <span className="text-terracotta-500"> {t({ de: "Zahlen aus.", en: "numbers look." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="font-body text-base text-sage-500/75 leading-relaxed">
                            {t({
                                de: "Wählen Sie eine Wohnung und ein Eigenkapital-Szenario. Wir zeigen Ihnen die aktuelle Rate, den monatlichen Cashflow, den Break-Even sowie den Netto-Vermögenszuwachs nach 30 Jahren. Alle Annahmen sind konservativ: 3,6 % Zins, 30 Jahre Laufzeit, 18 €/m² Kaltmiete, +15 % alle 3 Jahre, 2 % Wertsteigerung p.a.",
                                en: "Pick an apartment and an equity scenario. We'll show the monthly rate, cashflow, break-even year and net wealth gain after 30 years. Conservative assumptions: 3.6 % rate, 30 yrs, €18/sqm cold rent, +15 % every 3 yrs, 2 % appreciation p.a.",
                            })}
                        </p>
                    </div>
                </div>

                {/* Apartment selector */}
                <div className="flex flex-wrap gap-2 mb-6" role="tablist" data-testid="wealth-apt-selector">
                    {APARTMENTS.map((a) => (
                        <button
                            key={a.id}
                            type="button"
                            onClick={() => setAptId(a.id)}
                            className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                                aptId === a.id
                                    ? "border-terracotta-500 bg-terracotta-500 text-white"
                                    : "border-black/10 bg-white text-sage-700 hover:border-terracotta-500"
                            }`}
                            aria-pressed={aptId === a.id}
                            data-testid={`wealth-apt-${a.id}`}
                        >
                            <span className="font-display font-bold">{a.shortName || a.name}</span>
                            <span className="hidden sm:inline ml-2 text-xs opacity-70">
                                {a.size} m² · {fmtEUR(a.priceValue)}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Scenario selector */}
                <div className="flex flex-wrap gap-2 mb-10" role="tablist" data-testid="wealth-scenario-selector">
                    <span className="self-center font-body text-xs uppercase tracking-[0.18em] text-sage-500/60 mr-2">
                        {t({ de: "Eigenkapital:", en: "Equity:" })}
                    </span>
                    {SCENARIOS.map((s) => (
                        <button
                            key={s.id}
                            type="button"
                            onClick={() => setScenarioId(s.id)}
                            className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                                scenarioId === s.id
                                    ? "border-sage-600 bg-sage-600 text-white"
                                    : "border-black/10 bg-white text-sage-700 hover:border-sage-600"
                            }`}
                            aria-pressed={scenarioId === s.id}
                            data-testid={`wealth-scenario-${s.id}`}
                        >
                            {t({ de: s.labelDe, en: s.labelEn })}
                        </button>
                    ))}
                </div>

                {/* Result grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-black/5 border border-black/5 rounded-2xl overflow-hidden" data-testid="wealth-result-grid">
                    <StatBlock
                        label={t({ de: "Eigenkapital", en: "Equity" })}
                        value={fmtEUR(equity)}
                        detail={t({ de: "gewählt", en: "selected" })}
                    />
                    <StatBlock
                        label={t({ de: "Monatliche Rate", en: "Monthly rate" })}
                        value={fmtEUR(proj.monthly)}
                        detail={`3,6 %  ·  30 ${t({ de: "J", en: "y" })}`}
                    />
                    <StatBlock
                        label={t({ de: "Cashflow heute", en: "Cashflow today" })}
                        value={(be.rentNow - proj.monthly >= 0 ? "+" : "") + fmtEUR(be.rentNow - proj.monthly)}
                        detail={t({ de: "Kaltmiete − Rate", en: "cold rent − rate" })}
                        tone={be.rentNow - proj.monthly >= 0 ? "positive" : "warn"}
                    />
                    <StatBlock
                        label={t({ de: "Break-Even", en: "Break-even" })}
                        value={
                            be.reached === false
                                ? t({ de: "> 30 J", en: "> 30 yrs" })
                                : be.year === 0
                                ? t({ de: "ab Tag 1", en: "day 1" })
                                : `${t({ de: "Jahr", en: "year" })} ${be.year}`
                        }
                        detail={t({ de: "+15 % / 3 J", en: "+15 % / 3 yrs" })}
                        tone={be.reached === false ? "warn" : "positive"}
                    />
                </div>

                {/* ------------------------------------------------------------------ */}
                {/*  STEUEREFFEKT-RECHNER  (interaktiv)                                 */}
                {/* ------------------------------------------------------------------ */}
                <div
                    className="mt-10 grid lg:grid-cols-12 gap-px bg-black/5 border border-black/5 rounded-2xl overflow-hidden"
                    data-testid="wealth-tax-calc"
                >
                    {/* Left: inputs / slider */}
                    <div className="lg:col-span-5 bg-white p-8 lg:p-10">
                        <span className="overline text-terracotta-500">
                            {t({ de: "Bonus: Steuer-Effekt", en: "Bonus: Tax effect" })}
                        </span>
                        <h3 className="font-display font-bold text-3xl lg:text-4xl mt-3 text-balance leading-tight">
                            {t({ de: "Was der Staat", en: "What the tax office" })}
                            <span className="text-terracotta-500"> {t({ de: "zusteuert.", en: "contributes." })}</span>
                        </h3>
                        <p className="body-md mt-4 text-sage-500/80">
                            {t({
                                de: (
                                    <>
                                        Mit der <strong>5 % degressiven AfA</strong> (Neubau-EH40 ab 2026) + Schuldzinsen
                                        entsteht in Jahr 1 oft ein steuerlicher Verlust, der Ihre Einkommensteuer
                                        senkt. Stellen Sie Ihren persönlichen Grenzsteuersatz ein.
                                    </>
                                ),
                                en: (
                                    <>
                                        Thanks to the <strong>5 % declining-balance depreciation</strong> (new-build EH40 from 2026)
                                        plus mortgage interest, year 1 typically shows a taxable loss reducing your income tax.
                                        Enter your marginal tax rate.
                                    </>
                                ),
                            })}
                        </p>

                        <div className="mt-8">
                            <div className="flex items-baseline justify-between mb-3">
                                <label
                                    htmlFor="wealth-tax-slider"
                                    className="font-body text-xs uppercase tracking-[0.18em] text-sage-500/60"
                                >
                                    {t({ de: "Mein Grenzsteuersatz", en: "My marginal tax rate" })}
                                </label>
                                <span
                                    className="font-display font-bold text-2xl text-sage-700"
                                    data-testid="wealth-tax-rate-value"
                                >
                                    {taxRatePct} %
                                </span>
                            </div>
                            <input
                                id="wealth-tax-slider"
                                type="range"
                                min={25}
                                max={45}
                                step={1}
                                value={taxRatePct}
                                onChange={(e) => setTaxRatePct(Number(e.target.value))}
                                className="w-full accent-terracotta-500"
                                data-testid="wealth-tax-slider"
                            />
                            <div className="flex justify-between text-[10px] font-body uppercase tracking-[0.16em] text-sage-500/50 mt-2">
                                <span>25 %</span>
                                <span>35 %</span>
                                <span>45 %</span>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2" data-testid="wealth-tax-presets">
                                {[30, 36, 42, 45].map((r) => (
                                    <button
                                        key={r}
                                        type="button"
                                        onClick={() => setTaxRatePct(r)}
                                        className={`px-3 py-1 text-[11px] rounded-full border transition-colors ${
                                            taxRatePct === r
                                                ? "border-terracotta-500 bg-terracotta-500 text-white"
                                                : "border-black/10 bg-white text-sage-500/80 hover:border-terracotta-500"
                                        }`}
                                        aria-pressed={taxRatePct === r}
                                    >
                                        {r} %
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="mt-8 space-y-2">
                            <Line
                                label={t({ de: "Gebäudewert (80 % von Kauf + Stellplatz)", en: "Building share (80 % of price + parking)" })}
                                value={fmtEUR(tax.buildingShare)}
                                muted
                            />
                            <Line
                                label={t({ de: "AfA Jahr 1 (5 %)", en: "Depreciation year 1 (5 %)" })}
                                value={fmtEUR(tax.afaYear1)}
                                muted
                            />
                            <Line
                                label={t({ de: "Zinsen Jahr 1 (Näherung)", en: "Interest year 1 (approx.)" })}
                                value={fmtEUR(tax.interestYear1)}
                                muted
                            />
                            <Line
                                label={t({ de: "Jahresmiete (kalt)", en: "Annual cold rent" })}
                                value={"− " + fmtEUR(tax.annualRent)}
                                muted
                            />
                            <div className="pt-3 border-t border-black/10">
                                <Line
                                    label={t({ de: "Steuerlicher Verlust Jahr 1", en: "Taxable loss year 1" })}
                                    value={fmtEUR(tax.deductible)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right: headline saving number */}
                    <div className="lg:col-span-7 bg-terracotta-500 text-white p-8 lg:p-10 flex flex-col justify-between">
                        <div>
                            <span className="overline text-white/70">
                                {t({ de: "Einkommensteuer-Ersparnis · Jahr 1", en: "Income tax saving · year 1" })}
                            </span>
                            <div
                                className="font-display font-bold text-5xl lg:text-7xl mt-4 leading-[1.05]"
                                data-testid="wealth-tax-saving"
                            >
                                {tax.saving > 0 ? "~ " + fmtEUR(tax.saving) : fmtEUR(0)}
                            </div>
                            <p className="font-body text-sm text-white/80 mt-4 max-w-md leading-relaxed">
                                {t({
                                    de: (
                                        <>
                                            Persönliche Ersparnis bei <strong>{taxRatePct} %</strong> Grenzsteuersatz –
                                            berechnet aus <strong>{fmtEUR(tax.deductible)}</strong> steuerlichem
                                            Verlust × {taxRatePct} %. Reduziert Ihre effektive Belastung
                                            auf rund <strong>{fmtEUR(Math.max(0, proj.monthly - be.rentNow - tax.saving / 12))}</strong> pro Monat
                                            in Jahr 1.
                                        </>
                                    ),
                                    en: (
                                        <>
                                            Personal saving at <strong>{taxRatePct} %</strong> marginal rate –
                                            based on <strong>{fmtEUR(tax.deductible)}</strong> taxable loss × {taxRatePct} %.
                                            Reduces your effective burden to about <strong>{fmtEUR(Math.max(0, proj.monthly - be.rentNow - tax.saving / 12))}</strong> per month
                                            in year 1.
                                        </>
                                    ),
                                })}
                            </p>
                        </div>
                        <div className="mt-8 grid grid-cols-2 gap-px bg-white/10">
                            <div className="bg-terracotta-500 p-4">
                                <div className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">
                                    {t({ de: "Monatlich", en: "Monthly" })}
                                </div>
                                <div className="font-display font-bold text-2xl mt-1" data-testid="wealth-tax-saving-month">
                                    {fmtEUR(tax.saving / 12)}
                                </div>
                            </div>
                            <div className="bg-terracotta-500 p-4">
                                <div className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">
                                    {t({ de: "Über 5 Jahre", en: "Over 5 yrs" })}
                                </div>
                                <div className="font-display font-bold text-2xl mt-1" data-testid="wealth-tax-saving-5y">
                                    ~ {fmtEUR(tax.saving * 5)}
                                </div>
                            </div>
                        </div>
                        <p className="mt-6 text-[11px] font-body text-white/60 leading-relaxed">
                            {t({
                                de: "Näherung Jahr 1 · AfA degressiv sinkt in Folgejahren · Keine individuelle Steuerberatung · Konsultieren Sie Ihren Steuerberater.",
                                en: "Year-1 approximation · declining-balance AfA shrinks in later years · Not individual tax advice · Consult your tax advisor.",
                            })}
                        </p>
                    </div>
                </div>

                {/* Wealth payload */}
                <div className="mt-10 grid lg:grid-cols-2 gap-px bg-black/5 border border-black/5 rounded-2xl overflow-hidden">
                    <div className="bg-white p-8 lg:p-10">
                        <span className="overline text-terracotta-500">
                            {t({ de: "Nach 30 Jahren", en: "After 30 years" })}
                        </span>
                        <div className="mt-4 space-y-4">
                            <Line
                                label={t({ de: "Ihr Eigenkapital (eingesetzt)", en: "Equity invested" })}
                                value={fmtEUR(equity)}
                                muted
                            />
                            <Line
                                label={t({ de: "Tilgung (vom Mieter gezahlt)", en: "Amortisation (paid by tenant)" })}
                                value={fmtEUR(proj.paidDown)}
                            />
                            <Line
                                label={t({ de: "Wertsteigerung Immobilie", en: "Property appreciation" })}
                                value={fmtEUR(proj.appreciation)}
                            />
                            <Line
                                label={t({ de: "Kumulierter Cashflow", en: "Cumulative cashflow" })}
                                value={(proj.cashflowSum >= 0 ? "+" : "") + fmtEUR(proj.cashflowSum)}
                                muted
                            />
                            <div className="pt-4 border-t border-black/10">
                                <Line
                                    label={t({ de: "Netto-Vermögenszuwachs", en: "Net wealth gain" })}
                                    value={fmtEUR(proj.netWealth)}
                                    big
                                />
                            </div>
                            <p className="font-body text-xs text-sage-500/60 mt-3 leading-relaxed">
                                {t({
                                    de: "Bei 2 % p.a. Wertsteigerung · Kaltmiete-Indexierung 15 % alle 3 Jahre · vor Steuer, ohne AfA-Effekt, ohne Rücklagen/Leerstand · unverbindliche Indikation.",
                                    en: "Appreciation 2 % p.a. · rent indexing 15 % every 3 yrs · pre-tax, excl. depreciation, excl. reserves/vacancy · non-binding indication.",
                                })}
                            </p>
                        </div>
                    </div>
                    <div className="bg-sage-600 text-white p-8 lg:p-10 flex flex-col justify-between">
                        <div>
                            <span className="overline text-terracotta-300">
                                {t({ de: "Das Wichtigste", en: "The headline" })}
                            </span>
                            <div className="font-display font-bold text-5xl lg:text-6xl text-terracotta-300 mt-4" data-testid="wealth-headline-number">
                                {fmtEUR(proj.netWealth)}
                            </div>
                            <p className="font-body text-sm text-white/80 mt-3 leading-relaxed">
                                {t({
                                    de: (
                                        <>
                                            Zusätzliches <strong>Netto-Vermögen</strong> nach 30 Jahren gegenüber Ihrem eingesetzten
                                            Eigenkapital – bei <strong>{apt.name}</strong> mit{" "}
                                            <strong>{fmtEUR(equity)}</strong> EK. Rendite aufs Eigenkapital:{" "}
                                            <strong className="text-terracotta-300">
                                                × {(proj.netWealth / Math.max(1, equity)).toFixed(1)}
                                            </strong>.
                                        </>
                                    ),
                                    en: (
                                        <>
                                            Additional <strong>net wealth</strong> after 30 years compared to your equity –{" "}
                                            <strong>{apt.name}</strong> with <strong>{fmtEUR(equity)}</strong> equity.
                                            Return on equity: <strong className="text-terracotta-300">
                                                ×{(proj.netWealth / Math.max(1, equity)).toFixed(1)}
                                            </strong>.
                                        </>
                                    ),
                                })}
                            </p>
                        </div>
                        <div className="mt-8 flex items-start gap-3 p-4 rounded-2xl bg-terracotta-300/10 border border-terracotta-300/30">
                            <Sparkles size={16} className="text-terracotta-300 shrink-0 mt-0.5" />
                            <p className="font-body text-xs text-white/80 leading-relaxed">
                                {t({
                                    de: "Zusätzlich nicht eingerechnet: 5 % degressive AfA für Neubau-Effizienzhäuser 40 (ab 2026) + KfW-Zuschüsse. Diese reduzieren Ihre effektive Finanzierungsbelastung deutlich und erhöhen den Netto-Cashflow.",
                                    en: "Not included above: 5 % declining-balance depreciation for energy-40 new builds (from 2026) + KfW grants. These cut your effective financing burden noticeably and increase net cashflow.",
                                })}
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={() => setPlanOpen(true)}
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-terracotta-300 text-sage-700 font-body font-medium hover:bg-terracotta-200 transition-colors"
                            data-testid="wealth-plan-cta"
                        >
                            <MessageCircle size={16} />
                            {t({ de: "Diesen Plan per E-Mail erhalten", en: "Receive this plan by email" })}
                            <ArrowRight size={16} />
                        </button>
                        <p className="mt-2 text-[11px] font-body text-white/55 text-center">
                            {t({
                                de: "Als PDF · personalisiert · innerhalb weniger Minuten im Postfach",
                                en: "As PDF · personalised · in your inbox within minutes",
                            })}
                        </p>
                    </div>
                </div>
            </div>

            <WealthPlanDialog
                open={planOpen}
                onOpenChange={setPlanOpen}
                scenario={{
                    apartmentId: apt.id,
                    apartmentName: apt.name,
                    equity,
                    rate: DEFAULT_RATE,
                    years: DEFAULT_TERM,
                    rentPerM2: RENT_PER_M2,
                    taxRatePct,
                }}
            />
        </section>
    );
};

/* --------------------------------------------------------------- */
/*  WEALTH TIMELINE – per apartment, hardcoded overview              */
/* --------------------------------------------------------------- */
/*  WEALTH TIMELINE – per apartment, hardcoded overview              */
/* --------------------------------------------------------------- */

const WealthTimeline = () => {
    const t = useT();
    // pre-compute net wealth per apartment at 3 horizons, 100k equity scenario
    const equity = 100000;
    const rows = APARTMENTS.map((a) => {
        const size = parseSize(a.size);
        const y10 = projectWealth({
            price: a.priceValue, parking: a.parkingPrice, equity,
            rate: DEFAULT_RATE, years: DEFAULT_TERM,
            rentPerM2: RENT_PER_M2, sizeM2: size, horizonYears: 10,
        });
        const y20 = projectWealth({
            price: a.priceValue, parking: a.parkingPrice, equity,
            rate: DEFAULT_RATE, years: DEFAULT_TERM,
            rentPerM2: RENT_PER_M2, sizeM2: size, horizonYears: 20,
        });
        const y30 = projectWealth({
            price: a.priceValue, parking: a.parkingPrice, equity,
            rate: DEFAULT_RATE, years: DEFAULT_TERM,
            rentPerM2: RENT_PER_M2, sizeM2: size, horizonYears: 30,
        });
        return {
            id: a.id,
            name: a.shortName || a.name,
            full: a.name,
            size: a.size,
            price: a.priceValue,
            y10: y10.netWealth,
            y20: y20.netWealth,
            y30: y30.netWealth,
        };
    });

    return (
        <section className="py-24 sm:py-32" data-testid="wealth-timeline">
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-10 mb-10">
                    <div className="lg:col-span-5">
                        <span className="overline text-terracotta-500">
                            {t({ de: "Alle 4 Wohnungen im Zeitverlauf", en: "All 4 apartments over time" })}
                        </span>
                        <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight mt-4 text-balance">
                            {t({ de: "Ihr Vermögen nach", en: "Your wealth after" })}
                            <span className="text-terracotta-500"> 10 · 20 · 30 {t({ de: "Jahren", en: "years" })}.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="font-body text-base text-sage-500/75 leading-relaxed">
                            {t({
                                de: (
                                    <>
                                        Beispielrechnung mit <strong>100.000 € Eigenkapital</strong> pro Wohnung, identische Annahmen wie oben.
                                        Zahlen zeigen den <strong>zusätzlichen Netto-Vermögenszuwachs</strong> (Tilgung + Wertsteigerung +
                                        kumulierter Cashflow).
                                    </>
                                ),
                                en: (
                                    <>
                                        Example with <strong>€100,000 equity</strong> per apartment, same assumptions as above.
                                        Figures show the <strong>additional net wealth gain</strong> (amortisation + appreciation + cumulative cashflow).
                                    </>
                                ),
                            })}
                        </p>
                    </div>
                </div>

                <div className="overflow-x-auto" data-testid="wealth-timeline-table">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-black/10 text-[11px] font-body uppercase tracking-[0.16em] text-sage-500/60">
                                <th className="text-left py-4 pr-4 font-body">{t({ de: "Wohnung", en: "Apartment" })}</th>
                                <th className="text-right py-4 px-3 font-body">{t({ de: "Nach 10 J", en: "After 10 yrs" })}</th>
                                <th className="text-right py-4 px-3 font-body">{t({ de: "Nach 20 J", en: "After 20 yrs" })}</th>
                                <th className="text-right py-4 pl-3 font-body">{t({ de: "Nach 30 J", en: "After 30 yrs" })}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-b border-black/10 hover:bg-mint-50 transition-colors" data-testid={`wealth-timeline-row-${r.id}`}>
                                    <td className="py-5 pr-4">
                                        <div className="font-display font-bold text-xl text-sage-700">{r.full}</div>
                                        <div className="font-body text-xs text-sage-500/60 mt-1">
                                            {r.size} m² · {fmtEUR(r.price)}
                                        </div>
                                    </td>
                                    <td className="text-right px-3 py-5 font-display font-bold text-lg text-sage-500/80">{fmtEUR(r.y10)}</td>
                                    <td className="text-right px-3 py-5 font-display font-bold text-xl text-sage-700">{fmtEUR(r.y20)}</td>
                                    <td className="text-right pl-3 py-5 font-display font-bold text-2xl text-terracotta-600">{fmtEUR(r.y30)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-10 grid md:grid-cols-3 gap-px bg-black/5 border border-black/5 rounded-2xl overflow-hidden">
                    <Mini icon={<ShieldCheck size={18} className="text-terracotta-500" />} title={t({ de: "Inflationsschutz", en: "Inflation hedge" })} body={t({ de: "Miete und Immobilienwert steigen – Ihr Kredit bleibt nominal gleich.", en: "Rent and property value rise – your loan stays nominal." })} />
                    <Mini icon={<Clock size={18} className="text-terracotta-500" />} title={t({ de: "Zeit arbeitet für Sie", en: "Time works for you" })} body={t({ de: "Der Zinseszinseffekt zieht am Ende der Laufzeit kräftig an.", en: "Compounding kicks in strongest late in the term." })} />
                    <Mini icon={<CheckCircle2 size={18} className="text-terracotta-500" />} title={t({ de: "Eigentum statt Miete", en: "Own, don't rent" })} body={t({ de: "Am Ende der Laufzeit gehört die Immobilie Ihnen – mietfrei oder voll vermietet.", en: "At the end of the term the property is yours – rent-free or fully let." })} />
                </div>
            </div>
        </section>
    );
};

const FinanceCta = () => {
    const t = useT();
    return (
        <section className="py-24 sm:py-32" data-testid="wealth-cta">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="bg-sage-500 rounded-[2rem] sm:rounded-[3rem] p-10 sm:p-16 lg:p-20 relative overflow-hidden">
                    <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-mint-200/15 pointer-events-none" />
                    <div className="absolute -right-10 bottom-0 w-56 h-56 rounded-full bg-terracotta-300/15 pointer-events-none" />
                    <div className="relative grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <div className="flex items-center gap-3 mb-4">
                                <Handshake size={22} className="text-terracotta-300" />
                                <span className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-300">
                                    {t({ de: "Ihr nächster Schritt", en: "Your next step" })}
                                </span>
                            </div>
                            <h2 className="font-display font-black tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-tight text-balance">
                                {t({
                                    de: (
                                        <>
                                            Sprechen Sie mit unserem <span className="text-terracotta-300">Finanzierungspartner</span>.
                                        </>
                                    ),
                                    en: (
                                        <>
                                            Talk to our <span className="text-terracotta-300">financing partner</span>.
                                        </>
                                    ),
                                })}
                            </h2>
                            <p className="font-body text-white/75 mt-6 max-w-2xl leading-relaxed">
                                {t({
                                    de: "Wir vermitteln Ihnen ein unverbindliches, kostenfreies Beratungsgespräch. Unser Partner vergleicht Angebote mehrerer Banken, bindet KfW-Zuschüsse ein und strukturiert die Finanzierung passend zu Ihrem Szenario – egal ob 50.000 € oder 300.000 € Eigenkapital.",
                                    en: "We connect you with a non-binding, free consultation. Our partner compares offers from multiple banks, integrates KfW grants and structures financing to fit your scenario – whether €50,000 or €300,000 in equity.",
                                })}
                            </p>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <Link
                                    to="/kontakt"
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-sage-500 font-body font-medium hover:bg-mint-50 transition-colors"
                                    data-testid="wealth-cta-contact"
                                >
                                    <MessageCircle size={16} />
                                    {t({ de: "Beratungstermin anfragen", en: "Request consultation" })}
                                    <ArrowRight size={16} />
                                </Link>
                                <Link
                                    to="/investor"
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/40 text-white hover:bg-white/10 transition-colors"
                                    data-testid="wealth-cta-investor"
                                >
                                    {t({ de: "Zur Investor-Seite", en: "Investor page" })}
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9">
                            <div className="rounded-3xl border border-white/15 bg-white/5 backdrop-blur-sm p-8 space-y-5">
                                <Check text={t({ de: "4 Wohnungen – 4 Szenarien durchgerechnet", en: "4 apartments – 4 scenarios modelled" })} />
                                <Check text={t({ de: "Ab 50.000 € Eigenkapital möglich", en: "Possible from €50,000 equity" })} />
                                <Check text={t({ de: "Inkl. KfW, AfA und Inflationseffekt", en: "Incl. KfW, depreciation, inflation effect" })} />
                                <Check text={t({ de: "Direktverkauf vom Bauträger – maklerfrei", en: "Direct from developer – brokerage-free" })} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ============================= shared ui ============================ */

const Metric = ({ label, value, tone = "neutral" }) => (
    <div className="bg-sage-600 p-6">
        <div className="font-body text-[11px] uppercase tracking-[0.16em] text-white/55">{label}</div>
        <div className={`font-display font-bold text-3xl mt-2 ${tone === "danger" ? "text-rose-300" : tone === "positive" ? "text-emerald-300" : "text-white"}`}>
            {value}
        </div>
    </div>
);

const StatBlock = ({ label, value, detail, tone = "neutral" }) => (
    <div className="bg-white p-6 lg:p-8">
        <div className="font-body text-[11px] uppercase tracking-[0.16em] text-sage-500/60">{label}</div>
        <div
            className={`font-display font-bold text-3xl mt-2 ${
                tone === "positive" ? "text-emerald-600" : tone === "warn" ? "text-amber-600" : "text-sage-700"
            }`}
        >
            {value}
        </div>
        <div className="font-body text-xs text-sage-500/60 mt-1">{detail}</div>
    </div>
);

const Line = ({ label, value, muted = false, big = false }) => (
    <div className="flex items-baseline justify-between gap-3">
        <span className={`font-body text-sm ${muted ? "text-sage-500/60" : "text-sage-700"}`}>
            {label}
        </span>
        <span className={`font-display font-bold ${big ? "text-3xl text-terracotta-600" : muted ? "text-lg text-sage-500/80" : "text-xl text-sage-700"}`}>
            {value}
        </span>
    </div>
);

const Mini = ({ icon, title, body }) => (
    <div className="bg-white p-6">
        <div className="flex items-center gap-2">{icon}<span className="font-display font-bold text-base text-sage-700">{title}</span></div>
        <p className="body-md text-sm text-sage-500/80 mt-2">{body}</p>
    </div>
);

const Check = ({ text }) => (
    <div className="flex items-start gap-3">
        <CheckCircle2 size={18} className="text-terracotta-300 shrink-0 mt-0.5" />
        <span className="font-body text-sm text-white/85 leading-relaxed">{text}</span>
    </div>
);
